import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog, faPlus, faEdit, faSave, faTrash } from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../../utils/apiConfig';

function AccountSettingsManagement({ userId, accountId, accountName, onRefresh }) {
  const [settings, setSettings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingSetting, setEditingSetting] = useState(null);
  const [formData, setFormData] = useState({
    settingKey: '',
    settingValue: ''
  });

  useEffect(() => {
    if (accountId) {
      fetchSettings();
    }
  }, [accountId]);

  const fetchSettings = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(getApiUrl(`/api/account-admin/accounts/${accountId}/settings`), {
        params: { userId }
      });
      setSettings(response.data);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching account settings:', error);
      setError('Failed to load account settings. Please try again later.');
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleAddSetting = () => {
    setEditingSetting(null);
    setFormData({
      settingKey: '',
      settingValue: ''
    });
    setShowAddModal(true);
  };

  const handleEditSetting = (setting) => {
    setEditingSetting(setting);
    setFormData({
      settingKey: setting.setting_key,
      settingValue: setting.setting_value || ''
    });
    setShowAddModal(true);
  };

  const handleDeleteSetting = async (setting) => {
    if (!window.confirm(`Are you sure you want to delete the setting "${setting.setting_key}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.delete(
        getApiUrl(`/api/account-admin/accounts/${accountId}/settings/${setting.setting_key}`),
        {
          data: { userId }
        }
      );

      // Remove from local state
      setSettings(settings.filter(s => s.setting_key !== setting.setting_key));

      // Trigger refresh of all admin tables
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error deleting setting:', error);
      setError('Failed to delete setting: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await api.put(
        getApiUrl(`/api/account-admin/accounts/${accountId}/settings/${formData.settingKey}`),
        {
          userId,
          settingValue: formData.settingValue
        }
      );
      
      // Update local state
      if (editingSetting) {
        setSettings(settings.map(setting => 
          setting.setting_key === formData.settingKey ? response.data : setting
        ));
      } else {
        setSettings([...settings, response.data]);
      }
      
      setShowAddModal(false);
      
      // Trigger refresh of all admin tables
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error saving setting:', error);
      setError('Failed to save setting: ' + (error.response?.data?.error || error.message));
    }
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          {/* <div className="loading-spinner"></div> */}
          <p>Loading account settings...</p>
        </div>
      </div>
    );
  }

  if (error && !showAddModal) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <p>{error}</p>
          <button className="btn btn-primary" onClick={fetchSettings}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-card">
      <div className="admin-card-header">
        <h2 className="admin-card-title">
          <FontAwesomeIcon icon={faCog} className="icon" />
          {accountName} - Settings
        </h2>
        <button 
          className="btn btn-primary" 
          onClick={handleAddSetting}
        >
          <FontAwesomeIcon icon={faPlus} /> Add Setting
        </button>
      </div>
      
      {error && (
        <div className="alert alert-danger">{error}</div>
      )}
      
      {/* Add/Edit Setting Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h3>
                <FontAwesomeIcon icon={faCog} className="mr-2" />
                {editingSetting ? 'Edit Setting' : 'Add New Setting'}
              </h3>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Setting Key</label>
                  <input
                    type="text"
                    name="settingKey"
                    list="account-setting-suggestions"
                    className="form-control"
                    value={formData.settingKey}
                    onChange={handleInputChange}
                    required
                    disabled={editingSetting}
                  />
                  <datalist id="account-setting-suggestions">
                    <option value="group-edit-enabled" />
                    <option value="performance-v2.0" />
                    <option value="offers-enabled" />
                    <option value="enable-v3-ui-planning-all-roles" />
                    <option value="daily-refresh-stats-analyses" />
                    <option value="enable-management-overview" />
                  </datalist>
                  {formData.settingKey === 'daily-refresh-stats-analyses' && (
                    <small>Set to true to run Refresh stats &amp; analyses for this account every day at 2am Pacific. Campaigns that have ended or are paused are skipped. Analyses also need enable-performance-tab=true and run only on campaigns live in their platform. Missing or false runs it only when someone presses the button.</small>
                  )}
                  {formData.settingKey === 'enable-v3-ui-planning-all-roles' && (
                    <small>Set to true to open planning-mode campaigns in the new Grid UI for every role in this account, including Campaign Manager. Does not require enable-v3-ui. Missing or false keeps automatic routing disabled and preserves the existing admin button access. Other campaign types and account permissions are unchanged.</small>
                  )}
                  {formData.settingKey === 'enable-management-overview' && (
                    <small>Set to true to show the Management Briefing for this account: a leadership summary built from the account's latest V2 review, linking into the Analyst Overview. Missing or false hides the view and its data.</small>
                  )}
                  {formData.settingKey === 'offers-enabled' && (
                    <small>Set to true to enable Offers for this account. Missing or false disables the menu, page and API for all users, including super admins.</small>
                  )}
                  {formData.settingKey === 'group-edit-enabled' && (
                    <small>Set to true to enable Group Edit when multiple Google Ads campaigns are selected. Supports creative spreadsheets and YouTube video links. Missing or false disables the button and group creative upload API for this account.</small>
                  )}
                  {formData.settingKey === 'performance-v2.0' && (
                    <small>Set to true to enable extended performance metrics, metric overlays, campaign dashboards, and recommendation filters. Requires enable-performance-tab=true. Set to false to return to the existing Performance view.</small>
                  )}
                </div>
                
                <div className="form-group">
                  <label>Setting Value</label>
                  <textarea
                    name="settingValue"
                    className="form-control"
                    value={formData.settingValue}
                    onChange={handleInputChange}
                    rows="3"
                  />
                </div>
                
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    <FontAwesomeIcon icon={faSave} /> Save
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      
      {settings.length === 0 ? (
        <div className="empty-state">
          <FontAwesomeIcon icon={faCog} className="empty-state-icon" />
          <p className="empty-state-text">No settings defined for this account yet</p>
          <p className="empty-state-subtext">
            Add settings to configure this account's behavior.
          </p>
          <button 
            className="btn btn-primary" 
            onClick={handleAddSetting}
          >
            <FontAwesomeIcon icon={faPlus} /> Add First Setting
          </button>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Setting Key</th>
                <th>Value</th>
                <th>Last Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {settings.map(setting => (
                <tr key={setting.id}>
                  <td>{setting.setting_key}</td>
                  <td className="setting-value-cell">{setting.setting_value || '-'}</td>
                  <td>{setting.updated_at ? new Date(setting.updated_at).toLocaleString() : new Date(setting.created_at).toLocaleString()}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleEditSetting(setting)}
                        title="Edit Setting"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteSetting(setting)}
                        title="Delete Setting"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AccountSettingsManagement;
